/* @flow */

import { createSelector } from '@reduxjs/toolkit';
import {
  hydrateTopic,
  hydrateTopics,
  isOneOnOneTopic,
  getOneOnOneTopic,
  getTopicBasedOnInterest,
} from '@braindate/domain/lib/topic/util';

import {
  getEntities,
  parseIdFromProps,
} from 'src/shared/app/base/util/reduxUtils';

import type { Topic } from '@braindate/domain/lib/topic/type';
import type { State } from 'src/shared/core/type/reduxTypes';

// User topic

const _getUserTopics = (state, props) => {
  const { id: userId } = props;
  const { entities } = state.domain;
  const { topics } = entities;

  const result = [];

  for (const id in topics) {
    const p = topics[id];

    if (p.author === userId) {
      result.push(p.id);
    }
  }

  return result;
};

const _getUserActiveTopics = (state, props) => {
  const { id: userId, locale } = props;
  const { entities } = state.domain;
  const { topics } = entities;

  const result = [];

  const isBasedOnInterest = (topic: Topic) => {
    if (
      isOneOnOneTopic(topic) &&
      getTopicBasedOnInterest(getOneOnOneTopic(topic), locale)
    ) {
      return true;
    }
    return false;
  };

  for (const id in topics) {
    const p = topics[id];

    if (p.author === userId && !isBasedOnInterest(p)) {
      result.push(p.id);
    }
  }

  return result;
};

export const getUserTopics: (state: Object, props: Object) => any =
  createSelector([_getUserTopics, getEntities], (result, entities): any => {
    const topics = hydrateTopics(result, entities);

    return topics;
  });

export const getUserActiveTopics: (
  state: Object,
  props: { id: number, locale: string },
) => any = createSelector(
  [_getUserActiveTopics, getEntities],
  (result, entities): any => {
    const topics = hydrateTopics(result, entities);

    return topics;
  },
);

// Other user

const _getTopicsByAuthor = (state, { userId }) => {
  const { topics } = state.domain.entities;

  const result = [];

  for (const id in topics) {
    const topic = topics[id];

    if (topic.author === userId) {
      result.push(topic.id);
    }
  }

  return result;
};

export const getTopicsByAuthor: (state: Object, props: Object) => any =
  createSelector([_getTopicsByAuthor, getEntities], (result, entities): any => {
    const topics = hydrateTopics(result, entities);

    return topics;
  });

// Own topics

const _getOwnActiveTopicsData = (state) => state.app.topics.ownActive;
const _getOwnActiveTopicsCount = (state) =>
  _getOwnActiveTopicsData(state).count;
const _getOwnHiddenTopicsData = (state) => state.app.topics.ownHidden;
const _getOwnPendingTopicsData = (state) => state.app.topics.ownPending;
const _getOwnRejectedTopicsData = (state) => state.app.topics.ownRejected;
const _getUserTopicsData = (state) => state.app.topics.user;
const _getUsersTopicsData = (id) => (state) =>
  state.app.topics.users[id] || { result: [] };
const _getUserTopicsCount = (state) => _getUserTopicsData(state).count;
const _getUserTopicsIsLazyFetching = (state) =>
  _getUserTopicsData(state).isLazyFetching;

const _getOwnActiveTopicsResult = (state) =>
  _getOwnActiveTopicsData(state).result;
const _getOwnActiveTopicsNext = (state) => _getOwnActiveTopicsData(state).next;
const _getOwnActiveTopicsIsLazyFetching = (state) =>
  _getOwnActiveTopicsData(state).isLazyFetching;
const _getOwnHiddenTopicsResult = (state) =>
  _getOwnHiddenTopicsData(state).result;
const _getOwnPendingTopicsResult = (state) =>
  _getOwnPendingTopicsData(state).result;
const _getOwnRejectedTopicsResult = (state) =>
  _getOwnRejectedTopicsData(state).result;

const _areOwnActiveTopicsFetching = (state) =>
  _getOwnActiveTopicsData(state).isFetching;
const _areOwnHiddenTopicsFetching = (state) =>
  _getOwnHiddenTopicsData(state).isFetching;
const _areOwnPendingTopicsFetching = (state) =>
  _getOwnPendingTopicsData(state).isFetching;
const _areOwnRejectedTopicsFetching = (state) =>
  _getOwnRejectedTopicsData(state).isFetching;
const _areUserTopicsFetching = (state) => _getUserTopicsData(state).isFetching;

export const getOwnActiveTopics: (state: Object) => any = createSelector(
  [_getOwnActiveTopicsResult, getEntities],
  (result, entities): any => hydrateTopics(result, entities),
);

export const getOwnActiveTopicsNext: (state: Object) => number = createSelector(
  [_getOwnActiveTopicsNext],
  (result): string => result,
);

export const getOwnActiveTopicsIsLazyFetching: (state: Object) => number =
  createSelector(
    [_getOwnActiveTopicsIsLazyFetching],
    (result): boolean => result,
  );

export const getOwnActiveTopicsCount: (state: Object) => number =
  createSelector([_getOwnActiveTopicsCount], (result): number => result);

export const getOwnHiddenTopics: (state: Object) => any = createSelector(
  [_getOwnHiddenTopicsResult, getEntities],
  (result, entities): any => hydrateTopics(result, entities),
);

export const getOwnPendingTopics: (state: Object) => any = createSelector(
  [_getOwnPendingTopicsResult, getEntities],
  (result, entities): any => hydrateTopics(result, entities),
);

export const getOwnRejectedTopics: (state: Object) => any = createSelector(
  [_getOwnRejectedTopicsResult, getEntities],
  (result, entities): any => hydrateTopics(result, entities),
);

export const areOwnActiveTopicsFetching: (state: Object) => boolean =
  createSelector(
    [_areOwnActiveTopicsFetching],
    (result: boolean): boolean => result,
  );

export const areOwnHiddenTopicsFetching: (state: Object) => boolean =
  createSelector(
    [_areOwnHiddenTopicsFetching],
    (result: boolean): boolean => result,
  );

export const areOwnPendingTopicsFetching: (state: Object) => boolean =
  createSelector(
    [_areOwnPendingTopicsFetching],
    (result: boolean): boolean => result,
  );

export const areOwnRejectedTopicsFetching: (state: Object) => boolean =
  createSelector(
    [_areOwnRejectedTopicsFetching],
    (result: boolean): boolean => result,
  );

export const areUserTopicsFetching: (state: Object) => boolean = createSelector(
  [_areUserTopicsFetching],
  (result: boolean): boolean => result,
);

export const getUserTopicsCount: (state: Object) => number = createSelector(
  [_getUserTopicsCount],
  (result: number): number => result,
);

export const getUserTopicsIsLazyFetching: (state: Object) => number =
  createSelector(
    [_getUserTopicsIsLazyFetching],
    (result: boolean): boolean => result,
  );

export const getUsersTopicsNext: (
  userId: string | number,
) => (state: Object) => string = (userId: string | number) =>
  createSelector([_getUsersTopicsData(userId)], ({ next }): Object => next);

export const getUsersTopicsCount: (
  userId: string | number,
) => (state: Object) => number = (userId: string | number) =>
  createSelector([_getUsersTopicsData(userId)], ({ count }): Object => count);

export const getUsersTopicsIsLazyFetching: (
  userId: string | number,
) => (state: Object) => boolean = (userId: string | number) =>
  createSelector(
    [_getUsersTopicsData(userId)],
    ({ isLazyFetching }): Object => isLazyFetching,
  );

export const getUsersTopics: (userId: number) => (state: Object) => Topic[] = (
  userId: number,
) =>
  createSelector(
    [_getUsersTopicsData(userId), getEntities],
    ({ result }, entities): Object => hydrateTopics(result || [], entities),
  );

export const getUsersTopicsFetched: (
  userId: number,
) => (state: Object) => boolean = (userId: number) =>
  createSelector(
    [_getUsersTopicsData(userId)],
    ({ result }): Object => !!result,
  );

export const getUsersTopicsIsFetching: (
  userId: number,
) => (state: Object) => boolean = (userId: number) =>
  createSelector(
    [_getUsersTopicsData(userId)],
    ({ isFetching }): Object => isFetching,
  );

// Single topic

export const getTopic: (state: State, props: { id: number }) => ?Topic =
  createSelector([parseIdFromProps, getEntities], (id, entities) => {
    if (id) {
      return hydrateTopic(parseInt(id, 10), entities);
    }
  });
